import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Edit2, Save, X, Loader2, Sparkles, ZoomIn, ZoomOut, RotateCcw, Square, Move, Trash2, Palette, Download, Upload, DollarSign, Layers, BarChart3, Tag, Shield, CheckCircle, Settings, ExternalLink, Bug } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { updateNews, deleteNews } from "@/utils/graphqlClient";
import { supabase } from "@/integrations/supabase/client";
import { generateAnnotatedImageBase64 } from "@/utils/annotationImageGenerator";
import { VisualAssetEditorWrapper } from '@/components/VisualAssetEditor/VisualAssetEditorWrapper';
import { AssetViewerWrapper } from '@/components/VisualAssetEditor/AssetViewerWrapper';
import { VisualAssetEditorByIdWrapper } from '@/components/VisualAssetEditor/VisualAssetEditorByIdWrapper';
import DeleteConfirmationDialog from '@/components/common/DeleteConfirmationDialog';

// Helper functions to get display images from assets (same logic as AssetCard)
const getBeforeDisplayImageUrl = (item: NewsItem): string => {
  // Since we have direct asset_id relationship, just use the first asset if available
  const asset = item.assets?.[0];

  if (asset) {
    // Priority: cropped > original > legacy primary_image
    return asset.primary_cropped_url ||
           asset.primary_original_url ||
           asset.primary_image ||
           item.before_image_url || '';
  }

  // Fallback to legacy before_image_url
  return item.before_image_url || '';
};

const getAfterDisplayImageUrl = (item: NewsItem): string => {
  // Since we have direct asset_id relationship, just use the first asset if available
  const asset = item.assets?.[0];

  if (asset) {
    // Priority: secondary_cropped > secondary_original
    return asset.secondary_cropped_url ||
           asset.secondary_original_url ||
           item.after_image_url || '';
  }
  
  // Fallback to legacy after_image_url
  return item.after_image_url || '';
};

interface AnnotationRectangle {
  id: string;
  x: number; // X position relative to image
  y: number; // Y position relative to image
  width: number;
  height: number;
  color: string;
  label?: string;
  created_at: string;
}

interface ImageAnnotations {
  image_type: 'before' | 'after';
  zoom_level: number;
  pan_x: number; // Image pan position
  pan_y: number;
  rectangles: AnnotationRectangle[];
}

interface NewsItem {
  id: string;
  slug: string;
  page_id: string;
  version: string;
  before_version?: string;
  quarter?: string;
  hook?: string;
  description?: string;
  before_image_url?: string;
  after_image_url?: string;
  gif_url?: string;
  published: boolean;
  verified: boolean;
  created_at?: string;
  logo_url?: string;
  zoom_level?: number;
  annotations?: ImageAnnotations[];
  price_change?: boolean;
  plan_change?: boolean;
  metric_change?: boolean;
  discount_change?: boolean;
  features_change?: boolean;
  asset_id?: string;
  assets?: Array<{
    id: string;
    collection_id: string;
    type: string;
    page_id: string;
    slug: string;
    version: string;
    primary_image?: string;
    primary_original_url?: string;
    primary_cropped_url?: string;
    secondary_original_url?: string;
    secondary_cropped_url?: string;
    primary_viewport_state?: any;
    secondary_viewport_state?: any;
    header?: string;
    subheader?: string;
    comparison_mode?: string;
    secondary_version?: string;
    created_at: string;
    updated_at: string;
    company_slug?: string;
    logo_url?: string;
    page?: {
      id: string;
      pricing_page_url?: string;
    };
  }>;
}

interface NewsViewerModalProps {
  item: NewsItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onItemUpdate?: (updatedItem: NewsItem) => void;
  onItemDelete?: (deletedItemId: string) => void;
}

type AnnotationMode = 'drag' | 'draw';

interface DraggableImageProps {
  src: string;
  alt: string;
  title: string;
  zoom: number;
  containerWidth: number;
  containerHeight: number;
  onImageLoad?: (naturalWidth: number, naturalHeight: number) => void;
  annotations?: AnnotationRectangle[];
  onAnnotationsChange?: (annotations: AnnotationRectangle[]) => void;
  annotationMode?: AnnotationMode;
  isEditing?: boolean;
  annotationColor?: string;
  panPosition?: { x: number; y: number };
  onPanPositionChange?: (position: { x: number; y: number }) => void;
}

// Helper function to convert hex color to rgba with opacity
const hexToRgba = (hex: string, opacity: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  // Fallback for invalid hex colors
  return `rgba(255, 0, 0, ${opacity})`;
};

const DraggableImage: React.FC<DraggableImageProps> = ({
  src,
  alt,
  title,
  zoom,
  containerWidth,
  containerHeight,
  onImageLoad,
  annotations = [],
  onAnnotationsChange,
  annotationMode = 'drag',
  isEditing = false,
  annotationColor = '#ff0000',
  panPosition = { x: 0, y: 0 },
  onPanPositionChange
}) => {
  const [position, setPosition] = useState(panPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentRect, setCurrentRect] = useState<{x: number, y: number, width: number, height: number} | null>(null);
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update position when panPosition prop changes
  useEffect(() => {
    setPosition(panPosition);
  }, [panPosition]);

  useEffect(() => {
    // Reset position when image changes or zoom changes
    // Only auto-center if there's no saved pan position
    if (containerRef.current && imageSize.width > 0 && panPosition.x === 0 && panPosition.y === 0) {
      const actualContainerWidth = containerRef.current.clientWidth;
      const actualContainerHeight = containerRef.current.clientHeight;
      const zoomedWidth = imageSize.width * zoom;
      const zoomedHeight = imageSize.height * zoom;
      
      // Center the image if it's smaller than the container, otherwise position at top-left
      const centerX = zoomedWidth <= actualContainerWidth ? (actualContainerWidth - zoomedWidth) / 2 : 0;
      const centerY = zoomedHeight <= actualContainerHeight ? (actualContainerHeight - zoomedHeight) / 2 : 0;
      
      setPosition({ x: centerX, y: centerY });
    }
  }, [src, zoom, imageSize, containerWidth, panPosition]);

  const handleImageLoad = () => {
    if (imageRef.current) {
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;
      setImageSize({
        width: naturalWidth,
        height: naturalHeight
      });
      
      // Call the callback to inform parent component of image dimensions
      if (onImageLoad) {
        onImageLoad(naturalWidth, naturalHeight);
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (annotationMode === 'draw' && isEditing && containerRef.current) {
      // Start drawing a rectangle
      const rect = containerRef.current.getBoundingClientRect();
      const clientX = e.clientX - rect.left;
      const clientY = e.clientY - rect.top;

      // Convert client coordinates to image coordinates
      // The rendering formula is: clientPos = position + (imageCoord * zoom)
      // So: imageCoord = (clientPos - position) / zoom
      const imageX = (clientX - position.x) / zoom;
      const imageY = (clientY - position.y) / zoom;
      
      setIsDrawing(true);
      setDrawStart({ x: imageX, y: imageY });
      setCurrentRect({ x: imageX, y: imageY, width: 0, height: 0 });
    } else if (containerRef.current) {
      // Check if the image can be dragged (is larger than container)
      const actualContainerWidth = containerRef.current.clientWidth;
      const actualContainerHeight = containerRef.current.clientHeight;
      const zoomedWidth = imageSize.width * zoom;
      const zoomedHeight = imageSize.height * zoom;
      
      // Only start dragging if the image is larger than the container in at least one dimension
      if (zoomedWidth > actualContainerWidth || zoomedHeight > actualContainerHeight) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;

    if (isDrawing && annotationMode === 'draw' && isEditing) {
      // Update rectangle being drawn
      const rect = containerRef.current.getBoundingClientRect();
      const clientX = e.clientX - rect.left;
      const clientY = e.clientY - rect.top;

      // Convert client coordinates to image coordinates
      // The rendering formula is: clientPos = position + (imageCoord * zoom)
      // So: imageCoord = (clientPos - position) / zoom
      const imageX = (clientX - position.x) / zoom;
      const imageY = (clientY - position.y) / zoom;
      
      const width = imageX - drawStart.x;
      const height = imageY - drawStart.y;
      
      setCurrentRect({
        x: Math.min(drawStart.x, imageX),
        y: Math.min(drawStart.y, imageY),
        width: Math.abs(width),
        height: Math.abs(height)
      });
    } else if (isDragging && !imageRef.current) {
      return;
    } else if (isDragging) {
      // Handle image dragging
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Calculate zoomed image dimensions
      const zoomedWidth = imageSize.width * zoom;
      const zoomedHeight = imageSize.height * zoom;

      // Get actual container dimensions (accounting for border and padding)
      const actualContainerWidth = containerRef.current.clientWidth; // Excludes border
      const actualContainerHeight = containerRef.current.clientHeight; // Excludes border

      // Calculate boundaries to prevent dragging too far
      let maxX, minX, maxY, minY;
      
      if (zoomedWidth <= actualContainerWidth) {
        // If image is smaller than container, center it horizontally and don't allow panning
        const centerX = (actualContainerWidth - zoomedWidth) / 2;
        maxX = minX = centerX;
      } else {
        // If image is larger than container, allow panning within bounds
        maxX = 0;
        minX = actualContainerWidth - zoomedWidth;
      }
      
      if (zoomedHeight <= actualContainerHeight) {
        // If image is smaller than container, center it vertically and don't allow panning
        const centerY = (actualContainerHeight - zoomedHeight) / 2;
        maxY = minY = centerY;
      } else {
        // If image is larger than container, allow panning within bounds
        maxY = 0;
        minY = actualContainerHeight - zoomedHeight;
      }

      const boundedX = Math.max(minX, Math.min(maxX, newX));
      const boundedY = Math.max(minY, Math.min(maxY, newY));

      const newPosition = { x: boundedX, y: boundedY };
      setPosition(newPosition);
      
      // Notify parent component of position change
      if (onPanPositionChange) {
        onPanPositionChange(newPosition);
      }
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentRect && annotationMode === 'draw' && isEditing) {
      // Complete the rectangle if it's large enough
      if (currentRect.width > 5 && currentRect.height > 5) {
        const newAnnotation: AnnotationRectangle = {
          id: `rect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          x: currentRect.x,
          y: currentRect.y,
          width: currentRect.width,
          height: currentRect.height,
          color: annotationColor,
          created_at: new Date().toISOString()
        };


        if (onAnnotationsChange) {
          onAnnotationsChange([...annotations, newAnnotation]);
        }
      }

      setIsDrawing(false);
      setCurrentRect(null);
    }

    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setIsDrawing(false);
    setCurrentRect(null);
  };

  const deleteAnnotation = (annotationId: string) => {
    if (onAnnotationsChange) {
      onAnnotationsChange(annotations.filter(a => a.id !== annotationId));
    }
  };

  return (
    <div className="flex flex-col w-full max-w-full">
      <h4 className="text-sm font-medium mb-2 break-words">{title}</h4>
      <div
        ref={containerRef}
        className="relative border rounded-lg overflow-hidden bg-gray-50 max-w-full"
        style={{ 
          width: Math.min(containerWidth, window.innerWidth * 0.4), 
          height: containerHeight,
          cursor: (() => {
            if (annotationMode === 'draw' && isEditing) return 'crosshair';
            if (isDragging) return 'grabbing';
            
            // Check if image can be dragged
            if (containerRef.current && imageSize.width > 0) {
              const actualContainerWidth = containerRef.current.clientWidth;
              const actualContainerHeight = containerRef.current.clientHeight;
              const zoomedWidth = imageSize.width * zoom;
              const zoomedHeight = imageSize.height * zoom;
              
              if (zoomedWidth > actualContainerWidth || zoomedHeight > actualContainerHeight) {
                return 'grab';
              }
            }
            
            return 'default';
          })()
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="absolute select-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            maxWidth: 'none',
            maxHeight: 'none',
            width: imageSize.width || 'auto',
            height: imageSize.height || 'auto'
          }}
          onLoad={handleImageLoad}
          draggable={false}
        />
        
        {/* Render existing annotations */}
        {annotations.map(annotation => (
          <div
            key={annotation.id}
            className="absolute border-2 pointer-events-none"
            style={{
              left: position.x + (annotation.x * zoom),
              top: position.y + (annotation.y * zoom),
              width: annotation.width * zoom,
              height: annotation.height * zoom,
              borderColor: annotation.color,
              backgroundColor: hexToRgba(annotation.color, 0.15),
              borderStyle: 'dashed',
              zIndex: 10
            }}
          >
            {isEditing && (
              <button
                className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs pointer-events-auto hover:bg-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteAnnotation(annotation.id);
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        
        {/* Render current rectangle being drawn */}
        {currentRect && isDrawing && (
          <div
            className="absolute border-2 pointer-events-none"
            style={{
              left: position.x + (currentRect.x * zoom),
              top: position.y + (currentRect.y * zoom),
              width: currentRect.width * zoom,
              height: currentRect.height * zoom,
              borderColor: annotationColor,
              backgroundColor: hexToRgba(annotationColor, 0.15),
              borderStyle: 'dashed',
              zIndex: 10
            }}
          />
        )}
        
        {imageSize.width > 0 && imageSize.height > 0 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            {Math.round(imageSize.width * zoom)} × {Math.round(imageSize.height * zoom)} ({Math.round(zoom * 100)}%)
          </div>
        )}
      </div>
    </div>
  );
};

const NewsViewerModal: React.FC<NewsViewerModalProps> = ({ item, open, onOpenChange, onItemUpdate, onItemDelete }) => {
  const [isEditing, setIsEditing] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [editedHook, setEditedHook] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedPriceChange, setEditedPriceChange] = useState(false);
  const [editedPlanChange, setEditedPlanChange] = useState(false);
  const [editedMetricChange, setEditedMetricChange] = useState(false);
  const [editedDiscountChange, setEditedDiscountChange] = useState(false);
  const [editedFeaturesChange, setEditedFeaturesChange] = useState(false);
  const [editedPublished, setEditedPublished] = useState(false);
  const [editedVerified, setEditedVerified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isAssetEditMode, setIsAssetEditMode] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Prepare editor data for VisualAssetEditor
  const getEditorData = () => {
    const asset = item?.assets?.[0];

    // Use asset's comparison_mode if available, otherwise infer from image availability
    let viewMode = 'side_by_side'; // default fallback

    if (asset?.comparison_mode) {
      viewMode = asset.comparison_mode;
    } else {
      // Infer view mode based on image availability
      const hasSecondary = !!(asset?.secondary_cropped_url ||
                              asset?.secondary_original_url ||
                              item?.after_image_url);
      viewMode = hasSecondary ? 'side_by_side' : 'single';
    }

    return {
      aspectRatio: '1:1', // Default aspect ratio since crop_aspect_ratio is removed
      viewMode: viewMode,
      header: asset?.header || '',
      subheader: asset?.subheader || '',
      companyName: item?.slug || 'Company',
      companyLogo: item?.logo_url || asset?.logo_url || '',
      primaryImages: asset ? [
        {
          version: item?.before_version || 'before',
          url: asset.primary_original_url || asset.primary_image || getBeforeDisplayImageUrl(item) || ''
        }
      ] : [],
      secondaryImages: asset ? [
        {
          version: item?.version || 'current',
          url: asset.secondary_original_url || getAfterDisplayImageUrl(item) || ''
        }
      ] : [],
      images: [],
      debugData: null
    };
  };
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState(400);
  const [imageDimensions, setImageDimensions] = useState<{before?: {width: number, height: number}, after?: {width: number, height: number}}>({});
  const [fitToWidthZoom, setFitToWidthZoom] = useState(1);
  const [annotationMode, setAnnotationMode] = useState<AnnotationMode>('drag');
  const [annotationColor, setAnnotationColor] = useState('#ff0000');
  const [beforeAnnotations, setBeforeAnnotations] = useState<AnnotationRectangle[]>([]);
  const [afterAnnotations, setAfterAnnotations] = useState<AnnotationRectangle[]>([]);
  const [beforePanPosition, setBeforePanPosition] = useState({ x: 0, y: 0 });
  const [afterPanPosition, setAfterPanPosition] = useState({ x: 0, y: 0 });
  const [croppedUrls, setCroppedUrls] = useState<{ primary?: string; secondary?: string }>({});
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any>(null);
  const [autoSaveNewsAfterAsset, setAutoSaveNewsAfterAsset] = useState(false);
  const [editorState, setEditorState] = useState<{
    header: string;
    subheader: string;
    primary?: {
      zoom: number;
      pan_x: number;
      pan_y: number;
      annotations: Array<{
        x: number;
        y: number;
        width: number;
        height: number;
        color?: string;
      }>;
    };
    secondary?: {
      zoom: number;
      pan_x: number;
      pan_y: number;
      annotations: Array<{
        x: number;
        y: number;
        width: number;
        height: number;
        color?: string;
      }>;
    };
  }>({
    header: '',
    subheader: ''
  });

  // Track if we're in the initial loading phase to prevent overwriting loaded state
  const [isLoadingInitialState, setIsLoadingInitialState] = useState(false);

  // Handler for verified toggle with dependency logic
  const handleVerifiedChange = (checked: boolean) => {
    setEditedVerified(checked);
    // If verified is set to false, also set published to false
    if (!checked) {
      setEditedPublished(false);
    }
  };
  const contentRef = useRef<HTMLDivElement>(null);
  // Remove AssetImageEditorRef as we're using VisualAssetEditor now
  const { toast } = useToast();

  // Removed getCurrentViewportStates - asset viewport states are now handled by the asset editor



  useEffect(() => {
    if (item) {

      // Set edit mode based on verification status: edit mode for unverified, view mode for verified
      setIsAssetEditMode(!item.verified);
      setEditedHook(item.hook || '');
      setEditedDescription((item.description || '').replace(/\\n/g, '\n'));
      setEditedPriceChange(item.price_change || false);
      setEditedPlanChange(item.plan_change || false);
      setEditedMetricChange(item.metric_change || false);
      setEditedDiscountChange(item.discount_change || false);
      setEditedFeaturesChange(item.features_change || false);
      setEditedPublished(item.published ?? false);
      setEditedVerified(item.verified ?? false);
      // Use saved zoom level or default to fit-width
      setZoom(item.zoom_level || 1);
      setImageDimensions({}); // Reset image dimensions
      setFitToWidthZoom(1); // Reset fit zoom
      
      // Load saved annotations and pan positions from asset coordinates (priority) or news annotations (fallback)
      const asset = item.assets?.[0];

      // Initialize cropped URLs from asset data if available
      if (asset) {
        setCroppedUrls({
          primary: asset.primary_cropped_url,
          secondary: asset.secondary_cropped_url
        });
      }

      // Check if viewport states have actual annotation data
      const hasViewportAnnotations = asset && (
        (asset.primary_viewport_state?.annotations?.length > 0) ||
        (asset.secondary_viewport_state?.annotations?.length > 0)
      );

      // Initialize editor state
      const newEditorState = {
        header: asset?.header || '',
        subheader: asset?.subheader || '',
        primary: undefined as any,
        secondary: undefined as any
      };

      if (hasViewportAnnotations) {
        // Load from viewport state fields (preferred method)
        const primaryViewport = asset.primary_viewport_state;
        const secondaryViewport = asset.secondary_viewport_state;

        if (primaryViewport) {
          const primaryAnnotations = (primaryViewport.annotations || []).map((rect: any) => ({
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
            color: rect.color || '#84cc16'
          }));

          newEditorState.primary = {
            zoom: primaryViewport.zoom || 1,
            pan_x: primaryViewport.pan_x || 0,
            pan_y: primaryViewport.pan_y || 0,
            annotations: primaryAnnotations
          };


          // Backward compatibility - set legacy state
          setBeforeAnnotations(primaryViewport.annotations || []);
          setBeforePanPosition({
            x: primaryViewport.pan_x || 0,
            y: primaryViewport.pan_y || 0
          });
          // Use asset zoom if available
          if (primaryViewport.zoom && !item.zoom_level) {
            setZoom(primaryViewport.zoom);
          }
        } else {
          setBeforeAnnotations([]);
          setBeforePanPosition({ x: 0, y: 0 });
        }

        if (secondaryViewport) {
          newEditorState.secondary = {
            zoom: secondaryViewport.zoom || 1,
            pan_x: secondaryViewport.pan_x || 0,
            pan_y: secondaryViewport.pan_y || 0,
            annotations: (secondaryViewport.annotations || []).map((rect: any) => ({
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
              color: rect.color
            }))
          };

          // Backward compatibility - set legacy state
          setAfterAnnotations(secondaryViewport.annotations || []);
          setAfterPanPosition({
            x: secondaryViewport.pan_x || 0,
            y: secondaryViewport.pan_y || 0
          });
        } else {
          setAfterAnnotations([]);
          setAfterPanPosition({ x: 0, y: 0 });
        }

      } else if (item.annotations) {
        // Fallback to news annotations (legacy method)
        const beforeData = item.annotations.find(a => a.image_type === 'before');
        const afterData = item.annotations.find(a => a.image_type === 'after');

        const beforeAnns = beforeData?.rectangles || [];
        const afterAnns = afterData?.rectangles || [];

        // Load pan positions
        const beforePan = { x: beforeData?.pan_x || 0, y: beforeData?.pan_y || 0 };
        const afterPan = { x: afterData?.pan_x || 0, y: afterData?.pan_y || 0 };

        // Set legacy state
        setBeforeAnnotations(beforeAnns);
        setAfterAnnotations(afterAnns);
        setBeforePanPosition(beforePan);
        setAfterPanPosition(afterPan);

        // Set editor state from legacy data
        if (beforeData) {
          newEditorState.primary = {
            zoom: beforeData.zoom_level || 1,
            pan_x: beforePan.x,
            pan_y: beforePan.y,
            annotations: beforeAnns.map(rect => ({
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
              color: rect.color
            }))
          };
        }

        if (afterData) {
          newEditorState.secondary = {
            zoom: afterData.zoom_level || 1,
            pan_x: afterPan.x,
            pan_y: afterPan.y,
            annotations: afterAnns.map(rect => ({
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
              color: rect.color
            }))
          };
        }

      } else {
        // Annotation fields removed - set default empty state
        setBeforeAnnotations([]);
        setAfterAnnotations([]);

        // Set default pan positions
        setBeforePanPosition({ x: 0, y: 0 });
        setAfterPanPosition({ x: 0, y: 0 });
      }


      // Set the editor state for the VisualAssetEditor
      setEditorState(newEditorState);

      // Clear loading flag after state is set (with small delay to allow VisualAssetEditor to initialize)
      setTimeout(() => {
        setIsLoadingInitialState(false);
      }, 2000); // 2 second delay to allow VisualAssetEditor full initialization
      
      // Reset annotation mode
      setAnnotationMode('drag');
    }
  }, [item]);

  // Calculate container width based on available space
  useEffect(() => {
    const calculateWidth = () => {
      if (contentRef.current) {
        const contentWidth = contentRef.current.clientWidth;
        // Account for: gap between images (8px), any flex container margins
        // Also leave some buffer for potential scrollbar and padding
        const availableWidth = contentWidth - 24; // 8px gap + 16px safety buffer
        const imageWidth = Math.floor(availableWidth / 2);
        const finalWidth = Math.max(imageWidth, 200); // Minimum width of 200px
        
        
        setContainerWidth(finalWidth);
      }
    };

    // Use a timeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(calculateWidth, 100);
    window.addEventListener('resize', calculateWidth);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateWidth);
    };
  }, [open]);

  const calculateFitToWidthZoom = useCallback(() => {
    if (!containerWidth || Object.keys(imageDimensions).length === 0) return;
    
    // Find the largest image width to determine the zoom level
    let maxImageWidth = 0;
    if (imageDimensions.before) {
      maxImageWidth = Math.max(maxImageWidth, imageDimensions.before.width);
    }
    if (imageDimensions.after) {
      maxImageWidth = Math.max(maxImageWidth, imageDimensions.after.width);
    }
    
    if (maxImageWidth > 0) {
      // Account for border width (typically 2px) in container width calculation
      const usableWidth = containerWidth - 4; // 2px border on each side
      const newFitZoom = usableWidth / maxImageWidth;
      setFitToWidthZoom(newFitZoom);
      
      // Only auto-set zoom if we don't have a saved zoom level or if in editing mode
      if (!item?.zoom_level || isEditing) {
        setZoom(newFitZoom);
      }
    }
  }, [containerWidth, imageDimensions, item?.zoom_level, isEditing]);

  // Calculate fit-to-width zoom when container width or image dimensions change
  useEffect(() => {
    calculateFitToWidthZoom();
  }, [calculateFitToWidthZoom]);

  if (!item) return null;

  const handleCancel = () => {
    setEditedHook(item.hook || '');
    setEditedDescription(item.description || '');
    setEditedPriceChange(item.price_change || false);
    setEditedPlanChange(item.plan_change || false);
    setEditedMetricChange(item.metric_change || false);
    setEditedDiscountChange(item.discount_change || false);
    setEditedFeaturesChange(item.features_change || false);
    setEditedPublished(item.published ?? false);
    setEditedVerified(item.verified ?? false);
    onOpenChange(false);
  };

  const handleGenerateDescription = async () => {
    if (!editedHook.trim()) {
      toast({
        variant: "destructive",
        title: "Hook required",
        description: "Please enter a hook first to generate a description."
      });
      return;
    }

    if (!item.before_version || !item.version) {
      toast({
        variant: "destructive",
        title: "Missing version information",
        description: "Both before_version and current version are required for comparison."
      });
      return;
    }

    setIsGeneratingDescription(true);
    try {
      const prompt = `Describe the differences based on the following: ${editedHook}`;
      
      const { data, error } = await supabase.functions.invoke('ask-rag', {
        body: {
          slug: item.slug,
          version1: item.before_version,
          version2: item.version,
          query: prompt,
          chunkSize: 4000
        }
      });
      

      if (error) {
        throw new Error(error.message || 'Failed to generate description');
      }

      if (!data.success) {
        throw new Error(data.error || 'Ask-rag function failed');
      }

      if (data.description) {
        setEditedDescription(data.description);
        
        // Check if there were any warnings or partial issues
        const hasWarnings = data.metadata && (
          !data.metadata.version1Existed || 
          !data.metadata.version2Existed ||
          data.metadata.version1Results < 1000 ||
          data.metadata.version2Results < 1000
        );
        
        toast({
          title: hasWarnings ? "Description generated (with warnings)" : "Description generated",
          description: hasWarnings 
            ? "AI-generated description created, but some data may be limited. Check the analysis quality."
            : "AI-generated description has been added to the description field.",
          variant: hasWarnings ? "default" : "default"
        });
      } else {
        throw new Error('No description returned from ask-rag');
      }

    } catch (error: any) {
      console.error('Error generating description:', error);
      
      // Handle different types of errors with specific messages
      let errorMessage = "Failed to generate description using AI";
      
      if (error.message) {
        if (error.message.includes('token')) {
          errorMessage = "Text too long for processing. Please try with a shorter hook.";
        } else if (error.message.includes('No pricing content found')) {
          errorMessage = "No pricing content found for the specified versions. The versions may not exist in the database.";
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = "Network error. Please check your connection and try again.";
        } else if (error.message.includes('timeout')) {
          errorMessage = "Request timed out. The analysis is taking too long - please try again.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        variant: "destructive",
        title: "AI Generation Failed",
        description: errorMessage,
        duration: 6000 // Show error longer so user can read it
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3)); // Max 3x zoom
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25)); // Min 0.25x zoom
  };

  const handleResetZoom = () => {
    setZoom(fitToWidthZoom);
  };

  const handleImageLoad = (imageType: 'before' | 'after', width: number, height: number) => {
    setImageDimensions(prev => ({
      ...prev,
      [imageType]: { width, height }
    }));
  };

  const saveNewsData = async () => {
    // Build the update data for news
    const updateData: { hook?: string; description?: string; zoom_level?: number; annotations?: ImageAnnotations[]; price_change?: boolean; plan_change?: boolean; metric_change?: boolean; discount_change?: boolean; features_change?: boolean; published?: boolean; verified?: boolean; before_image_url?: string; after_image_url?: string } = {};
    if (editedHook !== item.hook) updateData.hook = editedHook;
    if (editedDescription !== item.description) updateData.description = editedDescription;
    if (editedPriceChange !== item.price_change) updateData.price_change = editedPriceChange;
    if (editedPlanChange !== item.plan_change) updateData.plan_change = editedPlanChange;
    if (editedMetricChange !== item.metric_change) updateData.metric_change = editedMetricChange;
    if (editedDiscountChange !== item.discount_change) updateData.discount_change = editedDiscountChange;
    if (editedFeaturesChange !== item.features_change) updateData.features_change = editedFeaturesChange;
    if (editedPublished !== item.published) updateData.published = editedPublished;

      // Always save the current zoom level when in edit mode
      if (zoom !== item.zoom_level) updateData.zoom_level = zoom;

      // Update before_image_url and after_image_url with cropped URLs from visual asset editor
      if (croppedUrls.primary && croppedUrls.primary !== item.before_image_url) {
        updateData.before_image_url = croppedUrls.primary;
      }
      if (croppedUrls.secondary && croppedUrls.secondary !== item.after_image_url) {
        updateData.after_image_url = croppedUrls.secondary;
      }

      // Build annotations data - ensure rectangles have required fields
      const annotations: ImageAnnotations[] = [];

      // Helper function to ensure rectangles have required fields
      const ensureRectangleFields = (rectangles: any[]): AnnotationRectangle[] => {
        return rectangles.map(rect => ({
          ...rect,
          id: rect.id || `rect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          created_at: rect.created_at || new Date().toISOString()
        }));
      };

      if (beforeAnnotations.length > 0 || getBeforeDisplayImageUrl(item)) {
        annotations.push({
          image_type: 'before',
          zoom_level: zoom,
          pan_x: beforePanPosition.x,
          pan_y: beforePanPosition.y,
          rectangles: ensureRectangleFields(beforeAnnotations)
        });
      }

      if (afterAnnotations.length > 0 || getAfterDisplayImageUrl(item)) {
        annotations.push({
          image_type: 'after',
          zoom_level: zoom,
          pan_x: afterPanPosition.x,
          pan_y: afterPanPosition.y,
          rectangles: ensureRectangleFields(afterAnnotations)
        });
      }

      // Always save annotations (even if empty) to clear them
      updateData.annotations = annotations;

      if (Object.keys(updateData).length === 0) {
        onOpenChange(false);
        return;
      }

      // Store the update data and show verification modal
      setPendingSaveData(updateData);
      setShowVerificationModal(true);
  };

  const handleSave = async () => {
    // If we're in asset edit mode, set flag to auto-save news after asset save
    if (isAssetEditMode) {
      setAutoSaveNewsAfterAsset(true);
      toast({
        title: "Asset editor is open",
        description: "Please save your asset changes first using the asset editor's save button. News will auto-save afterwards.",
        duration: 5000
      });

      // The asset editor will handle the save and trigger our onSave callback which will then save news
      return;
    }

    // If not in asset edit mode, save news directly
    await saveNewsData();
  };

  const performSave = async (verified: boolean) => {
    setIsSaving(true);
    setShowVerificationModal(false);

    try {
      // Add verification status to pending data
      const finalUpdateData = { ...pendingSaveData, verified };

      const response = await updateNews(item.id, finalUpdateData);

      // Asset updates are now handled inside the asset editor
      // No longer updating assets from NewsViewerModal

      const newsResponse = response as any; // Type assertion for GraphQL response
      const updatedItem: NewsItem = {
        ...item,
        hook: newsResponse.updateNews?.hook || item.hook,
        description: newsResponse.updateNews?.description || item.description,
        zoom_level: newsResponse.updateNews?.zoom_level || item.zoom_level,
        annotations: newsResponse.updateNews?.annotations || item.annotations,
        price_change: newsResponse.updateNews?.price_change ?? item.price_change,
        plan_change: newsResponse.updateNews?.plan_change ?? item.plan_change,
        metric_change: newsResponse.updateNews?.metric_change ?? item.metric_change,
        discount_change: newsResponse.updateNews?.discount_change ?? item.discount_change,
        published: newsResponse.updateNews?.published ?? item.published,
        verified: newsResponse.updateNews?.verified ?? item.verified,
        before_image_url: newsResponse.updateNews?.before_image_url || item.before_image_url,
        after_image_url: newsResponse.updateNews?.after_image_url || item.after_image_url,
        asset_id: newsResponse.updateNews?.asset_id || item.asset_id,
        assets: (newsResponse.updateNews?.assets && newsResponse.updateNews.assets.length > 0) ?
            newsResponse.updateNews.assets : item.assets
      };


      if (onItemUpdate) {
        onItemUpdate(updatedItem);
      }

      onOpenChange(false);
      toast({
        title: "News updated",
        description: "Hook, description, zoom level, and annotations have been updated successfully."
      });
    } catch (error: any) {
      console.error('Error updating news:', error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error.message || "Failed to update news item"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const downloadViewport = async (imageType: 'before' | 'after') => {
    try {
      const imageUrl = imageType === 'before' ? getBeforeDisplayImageUrl(item) : getAfterDisplayImageUrl(item);
      const annotations = imageType === 'before' ? beforeAnnotations : afterAnnotations;
      const panPosition = imageType === 'before' ? beforePanPosition : afterPanPosition;
      
      if (!imageUrl) {
        toast({
          variant: "destructive",
          title: "No image to download",
          description: `No ${imageType} image is available.`
        });
        return;
      }

      // Create a canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Load the image
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageUrl;
      });

      // Calculate viewport dimensions (same as container)
      const viewportWidth = containerWidth;
      const viewportHeight = 400; // Same as containerHeight

      // Set canvas size to viewport size
      canvas.width = viewportWidth;
      canvas.height = viewportHeight;

      // Calculate scaled image dimensions
      const scaledWidth = img.naturalWidth * zoom;
      const scaledHeight = img.naturalHeight * zoom;

      // Draw the image at the current pan position and zoom
      ctx.drawImage(
        img,
        panPosition.x, // x position (pan offset)
        panPosition.y, // y position (pan offset)
        scaledWidth,   // scaled width
        scaledHeight   // scaled height
      );

      // Draw annotations as dashed rectangles
      annotations.forEach(annotation => {
        ctx.strokeStyle = annotation.color;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]); // Dashed line pattern
        
        // Calculate annotation position relative to the viewport
        const rectX = panPosition.x + (annotation.x * zoom);
        const rectY = panPosition.y + (annotation.y * zoom);
        const rectWidth = annotation.width * zoom;
        const rectHeight = annotation.height * zoom;
        
        ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${item?.slug}-${imageType}-${item?.version}-annotated.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          toast({
            title: "Image downloaded",
            description: `${imageType} image with annotations saved successfully.`
          });
        }
      }, 'image/png');

    } catch (error) {
      console.error('Error downloading viewport:', error);
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "Failed to capture and download the viewport."
      });
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!item) return;

    setIsDeleting(true);
    try {
      const result = await deleteNews(item.id);

      if (!result.deleteNews?.success) {
        throw new Error(result.deleteNews?.message || 'Failed to delete news item');
      }

      // Close delete modal first
      setShowDeleteModal(false);

      // Call the parent callback to remove the item from the list
      if (onItemDelete) {
        onItemDelete(item.id);
      }

      toast({
        title: "News deleted successfully",
        description: result.deleteNews.message,
      });

      // Close main modal after a small delay to ensure state updates
      setTimeout(() => {
        onOpenChange(false);
      }, 100);

    } catch (error: any) {
      console.error('Error deleting news:', error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "Failed to delete news item"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePublish = async () => {

    if (!item || item.published) {
      return;
    }

    try {
      setIsPublishing(true);
      
      
      // Generate annotated images for both before and after if annotations exist
      let beforeAnnotatedImageBase64 = null;
      let afterAnnotatedImageBase64 = null;

      // Generate before annotated image if there are before annotations OR if zoom/pan has been adjusted
      const beforeImageUrl = getBeforeDisplayImageUrl(item);
      if (beforeImageUrl && (beforeAnnotations.length > 0 || zoom !== 1 || beforePanPosition.x !== 0 || beforePanPosition.y !== 0)) {
        beforeAnnotatedImageBase64 = await generateAnnotatedImageBase64(
          beforeImageUrl,
          beforeAnnotations,
          beforePanPosition,
          zoom,
          containerWidth,
          400 // containerHeight
        );
        
        if (beforeAnnotatedImageBase64) {
        } else {
        }
      }

      // Generate after annotated image if there are after annotations OR if zoom/pan has been adjusted
      const afterImageUrl = getAfterDisplayImageUrl(item);
      if (afterImageUrl && (afterAnnotations.length > 0 || zoom !== 1 || afterPanPosition.x !== 0 || afterPanPosition.y !== 0)) {
        afterAnnotatedImageBase64 = await generateAnnotatedImageBase64(
          afterImageUrl,
          afterAnnotations,
          afterPanPosition,
          zoom,
          containerWidth,
          400 // containerHeight
        );
        
        if (afterAnnotatedImageBase64) {
        } else {
        }
      }

      // Call publish-news function with generated images

      const { data, error } = await supabase.functions.invoke('publish-news', {
        body: { 
          news_id: item.id, 
          action: 'publish',
          before_annotated_image_base64: beforeAnnotatedImageBase64,
          after_annotated_image_base64: afterAnnotatedImageBase64
        }
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to publish news');
      }

      // Update the item in the parent component
      if (onItemUpdate) {
        onItemUpdate({ ...item, published: true });
      }

      toast({
        title: "News published successfully",
        description: `${data.slug} - ${data.version} has been published with ${
          (beforeAnnotatedImageBase64 ? 1 : 0) + (afterAnnotatedImageBase64 ? 1 : 0)
        } annotated images`,
      });

      // Close the modal after successful publish
      onOpenChange(false);

    } catch (error: any) {
      console.error('Error publishing news:', error);
      toast({
        variant: "destructive",
        title: "Publish failed",
        description: error.message || "Failed to publish news item"
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-full max-h-full h-screen w-screen p-0">
        <div className="flex flex-col h-full">
          {/* Content - Split into two panels */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left Panel - 40% width, always visible */}
            <div className="w-[40%] flex flex-col border-r border-gray-200 h-full relative bg-white z-10">
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  {/* Company Logo and Slug */}
                  <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <div className="flex-shrink-0">
                      <img
                        src={item.logo_url || item.assets?.[0]?.logo_url || '/placeholder-logo.svg'}
                        alt={`${item.slug} logo`}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-logo.svg';
                        }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{item.slug}</h3>
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex items-center gap-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                        title="Open pricing page"
                      >
                        <a
                          href={item.assets?.[0]?.page?.pricing_page_url || `/companies/${item.slug}/${item.version}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                          <span className="text-xs">Pricing Page</span>
                        </a>
                      </Button>
                    </div>
                  </div>

                  {/* Hook */}
                  <div className="w-full">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Hook ({editedHook.length})</h4>
                    <Input
                      value={editedHook}
                      onChange={(e) => setEditedHook(e.target.value)}
                      placeholder="Enter hook..."
                      className="text-lg font-semibold w-full"
                    />
                  </div>

                  {/* Description */}
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <h4 className="text-sm font-medium text-gray-700">Description ({editedDescription.length})</h4>
                    </div>
                    <Textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      placeholder="Enter description..."
                      rows={5}
                      className="resize-none w-full"
                    />
                  </div>

                  {/* Change Types */}
                  <div className="w-full">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Change Types</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="price_change"
                          checked={editedPriceChange}
                          onCheckedChange={(checked) => setEditedPriceChange(checked)}
                        />
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <label htmlFor="price_change" className="text-sm font-medium cursor-pointer">
                          Price Change
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="plan_change"
                          checked={editedPlanChange}
                          onCheckedChange={(checked) => setEditedPlanChange(checked)}
                        />
                        <Layers className="h-4 w-4 text-blue-600" />
                        <label htmlFor="plan_change" className="text-sm font-medium cursor-pointer">
                          Plan Change
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="metric_change"
                          checked={editedMetricChange}
                          onCheckedChange={(checked) => setEditedMetricChange(checked)}
                        />
                        <BarChart3 className="h-4 w-4 text-purple-600" />
                        <label htmlFor="metric_change" className="text-sm font-medium cursor-pointer">
                          Metric Change
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="discount_change"
                          checked={editedDiscountChange}
                          onCheckedChange={(checked) => setEditedDiscountChange(checked)}
                        />
                        <Tag className="h-4 w-4 text-orange-600" />
                        <label htmlFor="discount_change" className="text-sm font-medium cursor-pointer">
                          Discount Change
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="features_change"
                          checked={editedFeaturesChange}
                          onCheckedChange={(checked) => setEditedFeaturesChange(checked)}
                        />
                        <Settings className="h-4 w-4 text-indigo-600" />
                        <label htmlFor="features_change" className="text-sm font-medium cursor-pointer">
                          Features Change
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* State */}
                  <div className="w-full">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">State</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="verified"
                          checked={editedVerified}
                          onCheckedChange={handleVerifiedChange}
                        />
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <label htmlFor="verified" className="text-sm font-medium cursor-pointer">
                          Verified
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="publish_on_site"
                          checked={editedPublished}
                          disabled={!editedVerified}
                          onCheckedChange={(checked) => setEditedPublished(checked)}
                        />
                        <Shield className="h-4 w-4 text-blue-600" />
                        <label htmlFor="publish_on_site" className={`text-sm font-medium cursor-pointer ${!editedVerified ? 'text-gray-400' : ''}`}>
                          Publish on Site
                        </label>
                      </div>
                    </div>

                    {/* Delete Button */}
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        onClick={handleDeleteClick}
                        disabled={isDeleting || isSaving}
                        className="w-full flex items-center justify-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        {isDeleting ? 'Deleting...' : 'Delete News Item'}
                      </Button>
                    </div>
                  </div>

                  {/* Cropped URLs */}
                  {(croppedUrls.primary || croppedUrls.secondary) && (
                    <div className="w-full">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Cropped Images</h4>
                      <div className="flex gap-2 text-xs">
                        {croppedUrls.primary && (
                          <a
                            href={croppedUrls.primary}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-blue-700 hover:bg-blue-100 truncate"
                            title={croppedUrls.primary}
                          >
                            Primary
                          </a>
                        )}
                        {croppedUrls.secondary && (
                          <a
                            href={croppedUrls.secondary}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-green-700 hover:bg-green-100 truncate"
                            title={croppedUrls.secondary}
                          >
                            Secondary
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* GIF Visualization */}
                  {item.gif_url && (
                    <div className="w-full">
                      <h4 className="text-sm font-medium mb-3">Change Visualization</h4>
                      <div className="rounded-lg overflow-hidden border bg-gray-50 max-w-full">
                        <img
                          src={item.gif_url}
                          alt="Change visualization"
                          className="w-full h-auto max-h-60 object-contain max-w-full"
                        />
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="border-t border-gray-200 pt-6 mt-8">
                    <div className="space-y-3">
                      <Button
                        variant="default"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="w-full flex items-center justify-center gap-2"
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        {isSaving ? 'Saving...' : (isAssetEditMode ? 'Save Asset First' : 'Save Changes')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Right Panel - 60% width, always 60% width */}
            <div className="w-[60%] flex-1 overflow-hidden relative" ref={contentRef}>
              {isAssetEditMode ? (
                /* Asset Edit Mode - VisualAssetEditor contained in right panel */
                <div className="h-full w-full">
                  {item?.assets?.[0] && (
                    <VisualAssetEditorByIdWrapper
                      key={`asset-editor-${item.assets[0].id}-${isAssetEditMode}`}
                      assetId={item.assets[0].id}
                      className="w-full h-full"
                      initialViewportState={editorState}
                      onStateChange={(state) => {
                        // Capture cropped URLs from live state changes
                        if (state?.croppedUrls?.primaryCroppedUrl || state?.croppedUrls?.secondaryCroppedUrl) {
                          setCroppedUrls({
                            primary: state.croppedUrls.primaryCroppedUrl,
                            secondary: state.croppedUrls.secondaryCroppedUrl
                          });
                        }
                      }}
                      onSave={(data) => {
                        // Update the parent component if needed
                        if (onItemUpdate && item) {
                          onItemUpdate(item);
                        }
                        setIsAssetEditMode(false); // Return to view mode

                        // If auto-save news is flagged, continue with news save
                        if (autoSaveNewsAfterAsset) {
                          setAutoSaveNewsAfterAsset(false); // Reset flag
                          setTimeout(() => {
                            saveNewsData();
                          }, 100); // Small delay to ensure state updates
                        }
                      }}
                      onCancel={() => {
                        setIsAssetEditMode(false); // Return to view mode
                      }}
                      showSaveControls={true} // Show save controls but make them trigger via our save
                    />
                  )}
                </div>
              ) : (
                /* Asset Viewer in right panel */
                <div className="absolute inset-0 bg-white">
                  {item?.assets?.[0] && (
                    <AssetViewerWrapper
                      assetId={item.assets[0].id}
                      className="w-full h-full"
                      width="100%"
                      height="100%"
                      marginPct={0.05}
                      showEditButton={true}
                      onEdit={() => {
                        setIsAssetEditMode(true);
                      }}
                    />
                  )}
                </div>
              )}

              {/* Floating Debug Panel */}
              <div className="absolute top-2 left-2 z-50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                  className="mb-2 bg-white/90 backdrop-blur-sm"
                >
                  <Bug className="h-4 w-4 mr-1" />
                  Debug
                </Button>

                {showDebugPanel && (
                  <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg p-4 max-w-md max-h-96 overflow-y-auto">
                    <div className="space-y-4">
                      {/* Mode Status */}
                      <div>
                        <h4 className="font-semibold text-sm text-gray-800 mb-2">
                          {isAssetEditMode ? '📝 EDIT MODE DEBUG' : '👁️ VIEW MODE DEBUG'}
                        </h4>
                        <div className="text-xs space-y-1">
                          <div>Current Mode: <span className={`font-mono ${isAssetEditMode ? 'text-orange-600' : 'text-blue-600'}`}>{isAssetEditMode ? 'EDIT' : 'VIEW'}</span></div>
                          <div>Asset ID: <span className="font-mono">{item?.assets?.[0]?.id || 'N/A'}</span></div>
                          <div>Component Key: <span className="font-mono text-xs">{`asset-editor-${item?.assets?.[0]?.id}-${isAssetEditMode}`}</span></div>
                        </div>
                      </div>

                      {/* Asset Data */}
                      {item?.assets?.[0] && (
                        <div>
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">Asset Data</h4>
                          <div className="text-xs space-y-1">
                            <div>Type: <span className="font-mono">{item.assets[0].type}</span></div>
                            <div>Comparison Mode: <span className="font-mono">{item.assets[0].comparison_mode || 'N/A'}</span></div>
                            <div>Primary Original: <span className="font-mono text-green-600">{item.assets[0].primary_original_url ? 'Yes' : 'No'}</span></div>
                            <div>Primary Cropped: <span className="font-mono text-green-600">{item.assets[0].primary_cropped_url ? 'Yes' : 'No'}</span></div>
                            <div>Secondary Original: <span className="font-mono text-blue-600">{item.assets[0].secondary_original_url ? 'Yes' : 'No'}</span></div>
                            <div>Secondary Cropped: <span className="font-mono text-blue-600">{item.assets[0].secondary_cropped_url ? 'Yes' : 'No'}</span></div>
                          </div>
                        </div>
                      )}

                      {/* Viewport States */}
                      {item?.assets?.[0] && (
                        <div>
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">Viewport States</h4>
                          <div className="text-xs space-y-2">
                            <div>
                              <div className="font-medium text-green-700">Primary Viewport:</div>
                              {item.assets[0].primary_viewport_state ? (
                                <pre className="bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                                  {JSON.stringify(item.assets[0].primary_viewport_state, null, 2)}
                                </pre>
                              ) : (
                                <div className="text-gray-500 italic">No viewport state</div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-blue-700">Secondary Viewport:</div>
                              {item.assets[0].secondary_viewport_state ? (
                                <pre className="bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                                  {JSON.stringify(item.assets[0].secondary_viewport_state, null, 2)}
                                </pre>
                              ) : (
                                <div className="text-gray-500 italic">No viewport state</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mode-Specific Debug Info */}
                      {isAssetEditMode ? (
                        <div>
                          <h4 className="font-semibold text-sm text-orange-700 mb-2">VisualAssetEditorByIdWrapper Props</h4>
                          <div className="text-xs space-y-2">
                            <div>
                              <div className="font-medium">initialViewportState:</div>
                              <pre className="bg-orange-50 p-2 rounded mt-1 overflow-x-auto">
                                {JSON.stringify(editorState, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <div className="font-medium">showSaveControls:</div>
                              <span className="font-mono text-green-600">true</span>
                            </div>
                            <div>
                              <div className="font-medium">className:</div>
                              <span className="font-mono">w-full h-full</span>
                            </div>
                            <div>
                              <div className="font-medium">Expected Result:</div>
                              <span className="text-xs text-gray-600">Editor should load with annotations from viewport states</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-semibold text-sm text-blue-700 mb-2">AssetViewerWrapper Props</h4>
                          <div className="text-xs space-y-1">
                            <div>width: <span className="font-mono">100%</span></div>
                            <div>height: <span className="font-mono">100%</span></div>
                            <div>marginPct: <span className="font-mono">0.01</span></div>
                            <div>showEditButton: <span className="font-mono text-green-600">true</span></div>
                            <div>className: <span className="font-mono">w-full h-full</span></div>
                            <div>
                              <div className="font-medium mt-2">Expected Result:</div>
                              <span className="text-xs text-gray-600">Viewer should display asset with edit button overlay</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Cropped URLs State */}
                      <div>
                        <h4 className="font-semibold text-sm text-gray-800 mb-2">Cropped URLs</h4>
                        <div className="text-xs space-y-1">
                          <div>Primary: <span className="font-mono text-green-600">{croppedUrls.primary || 'N/A'}</span></div>
                          <div>Secondary: <span className="font-mono text-blue-600">{croppedUrls.secondary || 'N/A'}</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Verification Confirmation Modal */}
    <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
      <DialogContent className="max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Verification Status
          </h3>
          <p className="text-gray-600 mb-6">
            Do you consider this news item to be verified and accurate?
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => performSave(false)}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              No, Save Unverified
            </Button>
            <Button
              onClick={() => performSave(true)}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              Yes, Save as Verified
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Modal */}
    <DeleteConfirmationDialog
      isOpen={showDeleteModal}
      onOpenChange={setShowDeleteModal}
      onConfirm={handleDeleteConfirm}
      title="Delete News Item"
      description={`Are you sure you want to delete this news item for ${item?.slug?.replace(/-/g, ' ')} - ${item?.version}? This action cannot be undone.`}
      confirmLabel="Delete"
      cancelLabel="Cancel"
    />

    </>
  );
};

export default NewsViewerModal;